'use client'

import { Pip } from '@/design-system'

const MONO: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 500 }

const AUDIO_BARS = Array.from({ length: 64 }, (_, i) => {
  const v = Math.abs(Math.sin(i * 1.7) * 0.6 + Math.cos(i * 0.43) * 0.4)
  return Math.max(0.18, Math.min(1, v))
})

const AUDIO_COMMENTS = [
  { at: 0.18, t: '0:14', text: 'Bring vocal up here' },
  { at: 0.52, t: '0:42', text: 'Drop the reverb' },
  { at: 0.82, t: '1:05', text: 'Approved', approved: true }
] as const

export function AudioBody({ phase }: { phase: number }) {
  const playhead = phase
  const totalSec = 84
  const cur = Math.floor(playhead * totalSec)
  const curStr = `${Math.floor(cur / 60)}:${(cur % 60).toString().padStart(2, '0')}`
  const visibleComment = [...AUDIO_COMMENTS].reverse().find((c) => phase > c.at)

  const transport: { d: string; primary?: boolean }[] = [
    { d: 'M6 4 L6 20 M9 12 L20 4 L20 20 Z' },
    { d: 'M7 5 L7 19 L19 12 Z', primary: true },
    { d: 'M18 4 L18 20 M15 12 L4 4 L4 20 Z' }
  ]

  return (
    <div style={{ padding: 18, position: 'relative', minHeight: 320 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span
          style={{
            width: 44,
            height: 44,
            background: 'var(--mauve)',
            border: '2.5px solid var(--ink)',
            display: 'grid',
            placeItems: 'center'
          }}>
          <svg width={20} height={20} viewBox="0 0 24 24" fill="var(--ink)" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ ...MONO, fontSize: 12, fontWeight: 700 }}>master-cut-v3.wav</div>
          <div style={{ ...MONO, fontSize: 10, opacity: 0.6, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            WAV · 32 MB · 1:24
          </div>
        </div>
        <span style={{ ...MONO, fontSize: 11, opacity: 0.7 }}>{curStr} / 1:24</span>
      </div>

      <div
        style={{
          border: '2.5px solid var(--ink)',
          background: 'var(--paper)',
          padding: '14px 12px',
          position: 'relative',
          height: 110,
          marginBottom: 14
        }}>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
          {AUDIO_BARS.map((h, i) => {
            const past = i / AUDIO_BARS.length < playhead
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: `${h * 100}%`,
                  background: past ? 'var(--ink)' : 'rgba(10,10,10,0.22)',
                  transition: 'background .15s'
                }}
              />
            )
          })}
        </div>
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -4,
            bottom: -4,
            left: `calc(12px + ${playhead} * (100% - 24px))`,
            width: 2,
            background: 'var(--hot)',
            boxShadow: '0 0 0 1.5px var(--ink)'
          }}
        />
        {AUDIO_COMMENTS.map((c, i) => (
          <div
            key={i}
            aria-hidden
            style={{
              position: 'absolute',
              top: -8,
              left: `calc(12px + ${c.at} * (100% - 24px))`,
              transform: 'translateX(-50%)',
              width: 14,
              height: 14,
              background: 'var(--yellow)',
              border: '2px solid var(--ink)',
              opacity: phase > c.at - 0.02 ? 1 : 0.3,
              transition: 'opacity .2s'
            }}
          />
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        {transport.map((b, i) => (
          <button
            type="button"
            key={i}
            style={{
              width: 36,
              height: 36,
              border: '2.5px solid var(--ink)',
              background: b.primary ? 'var(--acid)' : 'var(--paper)',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              boxShadow: 'var(--shadow-sm)'
            }}>
            <svg
              width={18}
              height={18}
              viewBox="0 0 24 24"
              fill={b.primary ? 'var(--ink)' : 'none'}
              stroke="var(--ink)"
              strokeWidth={2.5}
              strokeLinejoin="round"
              aria-hidden>
              <path d={b.d} fill="var(--ink)" />
            </svg>
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <Pip bg="var(--paper-2)" style={{ fontSize: 9, padding: '2px 6px' }}>
          + COMMENT
        </Pip>
      </div>

      {visibleComment && (
        <div
          key={visibleComment.t}
          style={{
            border: '2px solid var(--ink)',
            background: 'approved' in visibleComment ? 'var(--acid)' : 'var(--yellow)',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: 'var(--shadow-sm)'
          }}>
          <span
            style={{
              ...MONO,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              background: 'var(--ink)',
              color: 'var(--paper)',
              padding: '2px 6px'
            }}>
            @{visibleComment.t}
          </span>
          <span style={{ ...MONO, fontSize: 11, flex: 1, opacity: 0.85 }}>&quot;{visibleComment.text}&quot;</span>
        </div>
      )}
    </div>
  )
}
